import React from 'react'
import { Icon, Button, Text, CardContainer } from '@adc/ui-morpheus'
import { constants } from '@adc/ui-core'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

// Separate component for comment textarea to prevent focus loss
interface CommentTextAreaProps {
  value: string
  onChange: (value: string) => void
  disabled: boolean
  onFocus?: () => void
}

// Uncontrolled textarea to prevent focus loss
const CommentTextArea = React.forwardRef<
  HTMLTextAreaElement,
  CommentTextAreaProps
>(({ value, onChange, disabled, onFocus }, ref) => {
  // Use internal ref to update value when it changes externally
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  // Combine refs
  React.useImperativeHandle(ref, () => textareaRef.current!)

  // Update textarea value when prop changes (e.g., loading new evaluation)
  React.useEffect(() => {
    if (textareaRef.current && textareaRef.current.value !== value) {
      textareaRef.current.value = value
    }
  }, [value])

  return (
    <textarea
      ref={textareaRef}
      id='user-rating-comment'
      defaultValue={value}
      onChange={e => onChange(e.target.value)}
      onFocus={onFocus}
      placeholder='Add your comments here...'
      rows={5}
      disabled={disabled}
      className={cx('comment-textarea')}
      style={{
        backgroundColor: disabled ? '#f8f9fa' : 'white',
      }}
    />
  )
})

CommentTextArea.displayName = 'CommentTextArea'

// Your Rating Section Component
interface CurrentUserRatingSectionProps {
  userRating: 'thumbsUp' | 'thumbsDown' | null
  userComment: string
  isSaving: boolean
  onThumbsUp: () => void
  onThumbsDown: () => void
  onCommentChange: (value: string) => void
  commentTextAreaRef: React.RefObject<HTMLTextAreaElement>
}

const CurrentUserRatingSection = React.memo<CurrentUserRatingSectionProps>(
  ({
    userRating,
    userComment,
    isSaving,
    onThumbsUp,
    onThumbsDown,
    onCommentChange,
    commentTextAreaRef,
  }) => {
    return (
      <CardContainer
        backgroundColor={constants.ColorNames.White}
        borderRadius='medium'
        padding='small'
        borderStyle='line'
        borderColor={constants.ColorNames.GrayLight}
      >
        <Text size='large' weight='heavy' className={cx('section-title')}>
          Your Evaluation
        </Text>

        <div className={cx('detail-row')} style={{ alignItems: 'center' }}>
          <Text size='small' color='gray'>
            Rating:
          </Text>
          <div className={cx('up-down-buttons')}>
            <Button
              style={{ marginTop: '-7px' }}
              role='tertiary'
              icon={
                <Icon
                  name='thumbs-up'
                  size='large'
                  iconStyle={userRating === 'thumbsUp' ? 'solid' : 'regular'}
                  color={userRating === 'thumbsUp' ? 'success' : 'gray'}
                />
              }
              onClick={onThumbsUp}
              size='small'
              elmId='user-rating-thumbs-up'
              collapse
              disabled={isSaving}
            />
            <Button
              role='tertiary'
              icon={
                <Icon
                  name='thumbs-down'
                  size='large'
                  iconStyle={userRating === 'thumbsDown' ? 'solid' : 'regular'}
                  color={userRating === 'thumbsDown' ? 'error' : 'gray'}
                />
              }
              onClick={onThumbsDown}
              size='small'
              elmId='user-rating-thumbs-down'
              collapse
              disabled={isSaving}
            />
          </div>
        </div>

        <div className={cx('comment-section')} style={{ marginTop: '12px' }}>
          <Text
            size='small'
            color='gray'
            style={{ marginBottom: '8px', display: 'block' }}
          >
            Comment (optional)
          </Text>
          <CommentTextArea
            ref={commentTextAreaRef}
            value={userComment}
            onChange={onCommentChange}
            disabled={!userRating || isSaving}
          />
        </div>
      </CardContainer>
    )
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent re-renders
    return (
      prevProps.userRating === nextProps.userRating &&
      prevProps.userComment === nextProps.userComment &&
      prevProps.isSaving === nextProps.isSaving &&
      prevProps.onThumbsUp === nextProps.onThumbsUp &&
      prevProps.onThumbsDown === nextProps.onThumbsDown &&
      prevProps.onCommentChange === nextProps.onCommentChange
    )
  }
)

CurrentUserRatingSection.displayName = 'CurrentUserRatingSection'

export default CurrentUserRatingSection
export { CommentTextArea }
